<template>
    <div>
        <!-- Type Array -->
        <template v-if="entry.type === 'array'">
            <template v-if="entry.additionalSchema === null">
                <r-input
                    :label="label"
                    help-text="E.g.: Red, Blue, Green"
                    :disabled="preview"
                    :value="arraySimpleValue"
                    @input="updateArraySimpleValue"
                />
            </template>
            <template v-else>
                <template
                    v-if="entry.additionalSchema.allowedValues.length > 0 && entry.additionalSchema.allowedValues.length <= 4">
                    <p class="stack-s">{{label}}</p>
                    <div class="grid-content">
                        <div class="grid-item grid-item-full" v-for="item in entry.additionalSchema.allowedValues"
                             :key="item">
                            <v-checkbox
                                :label="item"
                                :disabled="preview"
                                :value="item"
                                :selected="checkboxValue"
                                @input="updateSimpleValue"
                            />
                        </div>
                    </div>
                </template>
                <v-select
                    v-if="entry.additionalSchema.allowedValues.length > 4"
                    :label="label"
                    :disabled="preview"
                    :options="allowedValuesItems"
                    :hide-selected="true"
                    :close-on-select="false"
                    placeholder="Select"
                    :multiple="true"
                    :value="simpleValue"
                    @input="updateSimpleValue"
                />
            </template>
        </template>
        <!-- Type Boolean -->
        <template v-if="entry.type === 'boolean'">
            <v-checkbox
                :label="label"
                :disabled="preview"
                :value="true"
                :selected="simpleValue"
                @input="updateSimpleValue"
            />
        </template>
        <!-- Type Date -->
        <template v-if="entry.type === 'date'">
            <v-date-input
                :label="label"
                :value="date"
                :disabled="preview"
                :tab-index="previewTabIndex"
                @input="updateDate"
            />
        </template>
        <!-- Type Datetime -->
        <template v-if="entry.type === 'datetime'">
            <v-date-input
                :label="label"
                :value="datetime"
                type="datetime"
                :disabled="preview"
                @input="updateDatetime"
            />
        </template>
        <!-- Type Integer -->
        <template v-if="entry.type === 'integer'">
            <r-input
                :label="label"
                :value="simpleValue"
                help-text="E.g.: 42"
                :disabled="preview"
                @input="updateInteger"
                :validate="$v.localValue"
            />
        </template>
        <!-- Type Number -->
        <template v-if="entry.type === 'number'">
            <r-input
                :label="label"
                :value="simpleValue"
                help-text="E.g.: 42.22"
                :disabled="preview"
                @input="updateNumber"
                :validate="$v.localValue"
            />
        </template>
        <!-- Type String -->
        <template v-if="entry.type === 'string'">
            <template v-if="entry.additionalSchema === null">
                <r-input
                    :label="label"
                    :disabled="preview"
                    :value="simpleValue"
                    @input="updateSimpleValue"
                />
            </template>
            <template v-else-if="entry.additionalSchema.maxLength">
                <r-input
                    :label="label"
                    multiline
                    rows="3"
                    :maxLength="entry.additionalSchema.maxLength.toString()"
                    :disabled="preview"
                    :value="simpleValue"
                    :help-text="stringMaxLength"
                    @input="updateSimpleValue"
                />
            </template>
            <template v-else>
                <template
                    v-if="entry.additionalSchema.allowedValues.length > 0 && entry.additionalSchema.allowedValues.length <= 4">
                    <p class="stack-s">{{label}}</p>
                    <div class="grid-content">
                        <div class="grid-item grid-item-full" v-for="item in allowedValuesItems" :key="item">
                            <v-radio
                                :group="label"
                                :label="item"
                                :value="item"
                                :disabled="preview"
                                :model="value"
                                @update="updateSimpleValue"
                            />
                        </div>
                    </div>
                </template>
                <v-select
                    v-if="entry.additionalSchema.allowedValues.length > 4"
                    :label="label"
                    :disabled="preview"
                    :options="allowedValuesItems"
                    placeholder="Select"
                    :value="simpleValue"
                    @input="updateSimpleValue"/>
            </template>
        </template>
        <!-- Type Monetary -->
        <template v-if="entry.type === 'monetary'">
            <div class="field" :class="{'is-error': $v.$error}">
                <label class="field-label">{{label}}</label>
                <div class="field-group">
                    <v-select
                        placeholder="Select"
                        :options="currenciesList"
                        :value="monetary.currency"
                        :disabled="preview"
                        @input="updateCurrency"
                        class="no-flex custom-field-monetary-multiselect"/>
                    <input
                        ref="monetaryInput"
                        :disabled="preview"
                        class="field-input"
                        @input="updateAmount"
                        :value="localValue"
                        :validate="$v.localValue"
                    />
                </div>
                <span class="field-caption">E.g.: 50.1</span>
            </div>
        </template>
    </div>
</template>

<script>
    import {
        vCheckbox, vDateInput, vIcon, vRadio, vSelect,
    } from '@/components/form';
    import _ from '@/common/helpers';
    import {DateTimeFormats} from '@/common/datetime-formats';
    import CURRENCIES from '@/data/currencies';
    import moment from 'moment-timezone';
    import {numeric} from 'vuelidate/lib/validators';
    import decimal from 'vuelidate/lib/validators/decimal';

    export default {
        name: 'v-custom-field',
        components: {
            vIcon,
            vDateInput,
            vCheckbox,
            vRadio,
            vSelect,
        },
        props: {
            entry: {
                type: Object,
            },
            value: {
                type: [String, Object, Array, Boolean, Number],
                default: null,
            },
            preview: {
                type: Boolean,
                default: false,
            },
        },
        validations() {
            const map = {
                integer: {numeric},
                number: {decimal},
                monetary: {decimal},
            };
            return {
                localValue: {
                    ...map[this.entry.type],
                },
            };
        },
        watch: {
            value: {
                handler() {
                    if (this.value !== null && this.entry.type === 'monetary') {
                        this.localValue = this.value.amount;
                    }
                },
                immediate: true,
            },
        },
        data() {
            return {
                // `localValue` is an intermediary value to allow for
                // local validation without updating the parent value
                localValue: null,
            };
        },
        computed: {
            currenciesList() {
                return [...CURRENCIES].map(item => item.iso);
            },
            label() {
                return this.entry.name || 'Label';
            },
            allowedValuesItems() {
                if (this.entry.additionalSchema !== null) {
                    return this.entry.additionalSchema.allowedValues;
                }
                return null;
            },
            stringMaxLength() {
                if (this.entry.additionalSchema !== null) {
                    const {maxLength} = this.entry.additionalSchema;
                    const maxLengthLabel = this.value !== null ? maxLength - this.value.length : maxLength;
                    return `Number of characters allowed ${maxLengthLabel}`;
                }
                return null;
            },
            simpleValue() {
                return this.value;
            },
            checkboxValue() {
                return this.value || [];
            },
            arraySimpleValue() {
                return this.value && this.value.join(', ');
            },
            date() {
                return this.$tz().fromDate(this.value);
            },
            datetime() {
                if (this.value !== null) {
                    // since the `v-calendar` expects a date in the current timezone
                    // (regardless of user preference), we convert the UTC time into a
                    // localized Date instance, while keeping the original time/date
                    return moment.utc(this.value).tz(moment.tz.guess(), true);
                }
                return null;
            },
            monetary() {
                if (this.value !== null) {
                    return this.value;
                }
                return {
                    currency: '',
                    amount: 0,
                };
            },
            previewTabIndex() {
                return this.preview ? -1 : 0;
            },
        },
        methods: {
            updateSimpleValue(value) {
                const data = _.isBlank(value) ? null : value;
                this.$emit('input', data);
            },
            updateArraySimpleValue(value) {
                const data = _.isBlank(value) ? null : _.getCommaSeparated(value);
                this.$emit('input', data);
            },
            updateDate(date) {
                this.$emit('input', date.format(DateTimeFormats.datePickerDate));
            },
            updateDatetime(date) {
                this.$emit('input', date.tz('UTC', true).format());
            },
            updateInteger(value) {
                this.updateIfValid({
                    callback: () => parseInt(value, 10),
                    value,
                });
            },
            updateNumber(value) {
                this.updateIfValid({
                    callback: () => Number(value),
                    value,
                });
            },
            updateCurrency(value) {
                this.$refs.monetaryInput.focus();
                this.$emit('input', {
                    currency: value,
                    amount: Number(this.monetary.amount),
                });
            },
            updateAmount(event) {
                this.updateIfValid({
                    callback: () => ({
                        currency: this.monetary.currency,
                        // TODO fix case where amount is null
                        // which makes `updateIfValid` emit null
                        amount: Number(event.target.value),
                    }),
                    value: event.target.value,
                });
            },
            updateIfValid({callback, value}) {
                this.localValue = value;
                this.$v.$touch();
                if (!this.$v.$error) {
                    // send `null` when blank to prevent bad conversions
                    // from the parent to child components on the way back
                    const data = _.isBlank(value) ? null : callback();
                    this.$emit('input', data);
                }
            },
        },
    };
</script>
